import React, { ChangeEvent, FocusEvent, MutableRefObject, ReactNode, RefObject } from 'react';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';

import KEY_CODES from 'ia-react-core/~/constants/KEY_CODES';

import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';
import { FixedKeyboardEvent } from 'ia-react-core/~/interfaces/FixedKeyboardEvent';
import { ElementWithValue } from 'ia-react-core/~/interfaces/ElementWithValue';
import { WithInternapsValidatorsProps, WithValidatorsProps } from 'ia-react-core/~/hocs/form/#withValidators';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import compose from '../../../utils/components/#compose';
import merge from '../../../utils/accessors/merge';
import { StyledErrorMessages } from './Control.styles';
import {
  checkHasValidators, checkIsValid, resolveValue, resolveInitialValue, reduceTouched, reduceDirty, reduceErrors, reduceWarnings, reduceCautions,
} from './Control.utils';

export interface ValueToStateFormatter<Value, StateValue> {
  formatter: (value: Value, options: { options?: unknown }) => StateValue;
  deferred?: boolean;
  options?: unknown;
}

export interface StateToValueFormatter<Value, StateValue, E> {
  formatter: (value: StateValue, options: {
    event: FixedKeyboardEvent | ChangeEvent<E> | null;
    input: string | null;
    options?: unknown;
  }) => Value;
  deferred?: boolean;
  options?: unknown;
}

export interface ControlOnValueChangeOptions<E> {
  shouldCommit?: boolean;
  updateType?: 'blur' | 'change';
  event?: ChangeEvent<E>;
}

export interface ControlOnBlurOptions<E> {
  shouldCommit?: boolean;
  updateType?: 'blur' | 'change';
  event?: FocusEvent<E>;
}

export interface ControlStateToValueFormatOptions<E> {
  updateType?: 'blur' | 'change';
  event?: FixedKeyboardEvent | ChangeEvent<E>;
  input?: string;
}

export interface ControlValueToStateFormatOptions {
  updateType?: 'blur' | 'change';
}

export interface ControlUpdateValueOptions<Value, StateValue, E> {
  updateType?: 'blur' | 'change';
  shouldCommit?: boolean;
  displayValue?: Value | StateValue;
  event?: FixedKeyboardEvent | ChangeEvent<E>;
}

export interface ControlOnTouchedOptions {
  shouldCommit?: boolean;
}

export interface ControlUpdateInvalidValueOptions<Value, StateValue> {
  shouldCommit?: boolean;
  displayValue?: Value | StateValue;
}

export interface ControlUpdateStateOptions {
  updateType?: 'blur' | 'change';
}

export interface ControlReduceUpdatedStateOptions {
  updateType?: 'blur' | 'change';
}

export interface ControlReduceTouchedOptions {
  updateType?: 'blur' | 'change';
}

export interface ControlFormState {
  errors?: ErrorMap;
  warnings?: ErrorMap;
  cautions?: ErrorMap;
  touched?: boolean;
  dirty?: boolean;
}

interface ControlState<Value> {
  formattedValue: Value;
}

export interface ControlRenderProps<Value, E> {
  id: string;
  name: string;
  value: Value;
  errors: ErrorMap;
  warnings: ErrorMap;
  cautions: ErrorMap;
  isValid: boolean;
  hasWarning?: boolean;
  hasCaution?: boolean;
  isTouched: boolean;
  validateInitialValue?: boolean;
  isDirty: boolean;
  readOnly?: boolean;
  textOnly?: boolean;
  label?: string;
  errorMessagesId?: string;
  inputRef?: MutableRefObject<E>;
  isUnionControlGroup?: boolean;
  onChange: (event: ChangeEvent<ElementWithValue<Value>>) => void;
  onKeyDown: (event: FixedKeyboardEvent) => void;
  onValueChange: (value: Value, options?: ControlOnValueChangeOptions<E>) => void;
  onBlur: (event: FocusEvent<ElementWithValue<Value>>, options?: ControlOnBlurOptions<E>) => void;
  onTouched: (value: Value, options?: ControlOnTouchedOptions) => void;
}

export interface ControlProps<
  Value,
  StateValue,
  E extends Element = HTMLInputElement,
  V extends Record<string, unknown> = {},
> extends WithValidatorsProps<Value | StateValue, V> {
  /** The name of the control. Used as the key in the parent form. */
  name: string;
  /** A function called with the control state and methods, see below. */
  children?: (renderProps: ControlRenderProps<Value, E>) => ReactNode;
  /** Value to use at the control initialization. Comes usually from the state. */
  initialValue?: StateValue;
  /** Trigger validations for the initial value. */
  validateInitialValue?: boolean;
  /** Value to use when the control is invalid. */
  invalidValue?: StateValue | null;
  /** Default value of the control. Used when the value undefined. */
  defaultValue?: StateValue | Value;
  /** Allows invalid valid to being saved on the redux Store. */
  allowInvalid?: boolean;
  /** Maxium number of text lines to display. The remaining will appear on hover in a toolip. */
  maxErrorMessageRows?: number;
  /** Map of validation key/error message to override the default messages. */
  validationMessages?: MessageMap<Value | StateValue>;
  /** A function to transform the value into the state. */
  formatValueToState?: (value: Value | StateValue) => StateValue | Value;
  /** A function to transform the state into the value. */
  formatStateToValue?: (value: StateValue | Value) => Value;
  /** An array of formatters to apply when the value is committed to the state. */
  valueToStateFormatters?: Array<ValueToStateFormatter<Value, StateValue> | ((value: Value) => StateValue)>;
  /** An array of formatters to apply when state value is displayed. */
  stateToValueFormatters?: Array<StateToValueFormatter<Value, StateValue, E> | ((value: StateValue) => Value)>;
  /** A function to transform the state into the value. */
  updateOn?: Array<'change' | 'blur'>;
  /** Hide the error messages. */
  hideErrorMessages?: boolean | string[];
  /** Label that identifies the control. Usually inherited from the parent FormCol. Required if you hoist the errors and want a shortcut that points to the control. */
  colLabel?: string;
  /** Indicates the control is in readOnly mode. */
  readOnly?: boolean;
  /** Indicates the control is in textOnly mode. */
  textOnly?: boolean;
  /** Margin above the errors. */
  errorsMarginTop?: string;
  /** Hide any hoisted message anchor for this control. */
  hideHoistedAnchor?: boolean;
  /** Force the control to show has invalid. Does not affect the control validity. */
  forceInvalid?: boolean;
  /** Force the control to show with warning. Does not affect the control validity. */
  forceWarning?: boolean;
  /** Force the control to show with caution. Does not affect the control validity. */
  forceCaution?: boolean;
  /** Function to register the control on a group. */
  registerControlGroup?: (options: {
    name: string;
    id: string;
    controlId: number;
    validationMessages: ControlProps<Value, StateValue>['validationMessages'];
    label: ControlProps<Value, StateValue>['colLabel'];
    errorMessagesId: string;
    formScope: string[];
    hideHoistedAnchor: boolean;
    validators: ValidatorMap<any>;
  }) => void;
  /** Function to unregister the control on a group. */
  unregisterControlGroup?: (controlId: number) => void;
  /** @ignore */
  id?: string;
}

interface ControlInternalProps<
  Value,
  StateValue,
  V extends Record<string, unknown> = {},
> extends WithInternapsValidatorsProps<Value | StateValue, V> {
  value: StateValue;
  controlId: number;
  isSubmitted?: boolean;
  isInitialized?: boolean;
  isTouched?: boolean;
  validateInitialValue?: boolean;
  isDirty?: boolean;
  errors?: Record<string, boolean>;
  warnings?: Record<string, boolean>;
  cautions?: Record<string, boolean>;
  clientErrors: ErrorMap;
  clientWarnings: ErrorMap;
  clientCautions: ErrorMap;
  isControlGroup: boolean;
  isUnionControlGroup: boolean;
  formReducerKey: string;
  ancestorValuesDst: string;
  formReducerScope: string[];
  formScope: string[];
  ancestorFormReducerKey: string;
  ancestorFormReducerScope: string[];
  grouped: boolean;
  setControlValue: (name: string, value: StateValue | Value, callback: () => void) => void;
  setControlState: (name: string, state: ControlFormState) => void;
  registerControl: (options: {
    name: string;
    controlId: number;
    id: string;
    label: ControlProps<Value, StateValue>['colLabel'];
    validate: ControlComposedProps<Value, StateValue>['validate'];
  }) => void;
  unregisterControl: (options: {
    name: string;
    controlId: number;
  }) => void;
}

export interface ControlComposedProps<
  Value,
  StateValue,
  E extends Element = Element,
> extends Omit<ControlProps<Value, StateValue, E>, 'validate'>, ControlInternalProps<Value, StateValue> {}

const ControlDefaultProps = {
  defaultValue: '',
  invalidValue: null as unknown,
  allowInvalid: false,
  updateOn: ['change', 'blur'],
  isSubmitted: false,
  validateInitialValue: false,
  hideHoistedAnchor: false,
  forceInvalid: false,
  forceWarning: false,
  forceCaution: false,
};

/**
 * @mode developer
 */
class Control<
  Value,
  StateValue,
  E extends Element = HTMLInputElement
> extends React.Component<ControlComposedProps<Value, StateValue, E>, ControlState<Value>> {
  rerender: boolean;
  errorMessagesId: string;
  inputRef: RefObject<E>;
  preCommittedValue: StateValue | Value;
  uncommittedValue: Value | StateValue;
  lastCommittedValue: StateValue | Value;
  resolvedInitialValue: Value | StateValue;
  keyDownValue: string;
  keyDownCode: number;
  pendingCaretChange: number;
  lastRender: ReactNode;
  caretPositionRequest: number;
  id: string;

  constructor(props: ControlComposedProps<Value, StateValue, E>) {
    super(props);

    this.id = props.id || `Control${props.controlId}`;
    this.errorMessagesId = `${this.id}ErrorMessages`;
    this.inputRef = React.createRef();
    this.registerControlWithParentForm();
    this.setInitialFormValue();
    this.setInitialControlState();
    this.setInitialFormState();
  }

  shouldComponentUpdate(prevProps: ControlComposedProps<Value, StateValue, E>, prevState: ControlState<Value>) {
    this.rerender = this.shouldComponentRerender(prevProps, prevState);
    return true;
  }

  componentDidUpdate(prevProps: ControlComposedProps<Value, StateValue, E>) {
    const { stateToValueFormatters, initialValue } = this.props;
    // The initialValue may change when a form is submitted
    if (!isEqual(prevProps.initialValue, initialValue)) {
      const resolvedValue = this.getResolvedValue();
      this.resolvedInitialValue = this.getResolvedInitialValue();
      const newState = this.reduceDirty({}, resolvedValue as StateValue);
      if ('dirty' in newState) {
        this.commitControlState(newState);
      }
    }

    if (stateToValueFormatters !== prevProps.stateToValueFormatters) {
      this.refreshValueFormat();
    }

    const hasUpdated = this.handleValueUpdate(prevProps);
    if (!hasUpdated) {
      this.handleValidationChange(prevProps);
    }
    this.handleSubmittedChange(prevProps);
  }

  componentWillUnmount() {
    this.unregisterControl();
  }

  getResolvedInitialValue() {
    const { value, initialValue, defaultValue, invalidValue } = this.props;
    return resolveInitialValue<Value, StateValue>({ value, initialValue, defaultValue, invalidValue });
  }

  getResolvedValue() {
    const { isInitialized, value, defaultValue } = this.props;
    const { uncommittedValue } = this;
    const { resolvedInitialValue } = this;
    const { preCommittedValue } = this;

    return resolveValue({ value, defaultValue, resolvedInitialValue, uncommittedValue, preCommittedValue, isInitialized });
  }

  getEventInput(event: ChangeEvent<HTMLInputElement>) {
    if (!event || !event.nativeEvent || event.nativeEvent.type !== 'input' || !this.keyDownValue) {
      return null;
    }

    const { target } = event;
    const { selectionStart } = target;
    const inputDifference = target.value.length - this.keyDownValue.length;
    const inputStart = selectionStart - inputDifference;

    const input = (
      this.keyDownValue.length < target.value.length &&
      target.value.substring(inputStart, selectionStart)
    );

    return input;
  }

  setInitialFormValue() {
    const { initialValue } = this.props;
    this.resolvedInitialValue = this.getResolvedInitialValue();

    this.preCommittedValue = undefined;

    if (!isEqual(initialValue, this.resolvedInitialValue)) {
      this.commitControlValue(this.resolvedInitialValue);
    } else {
      this.lastCommittedValue = initialValue;
    }
  }

  setInitialControlState() {
    const resolvedValue = this.getResolvedValue();

    this.rerender = true;
    this.uncommittedValue = undefined;

    this.state = {
      formattedValue: this.formatStateToValue(resolvedValue),
    };
  }

  setInitialFormState() {
    const { isSubmitted, isTouched, isDirty } = this.props;

    const newState: ControlFormState = { dirty: Boolean(isDirty), touched: Boolean(isSubmitted || isTouched) };

    const { errors: newErrors, warnings: newWarnings, cautions: newCautions } = this.validate(this.resolvedInitialValue);

    if (checkHasValidators(newErrors)) {
      newState.errors = newErrors;
    }

    if (checkHasValidators(newWarnings)) {
      newState.warnings = newWarnings;
    }

    if (checkHasValidators(newCautions)) {
      newState.cautions = newCautions;
    }

    this.commitControlState(newState);
  }

  validate = (value: Value | StateValue) => {
    const { validate } = this.props;
    const { errors, warnings, cautions } = validate(value);
    return { errors, warnings, cautions };
  };

  handleChange = (event: ChangeEvent<ElementWithValue<Value>>) => {
    const { updateOn } = this.props;
    const { value } = event.target;

    const shouldCommit = (
      updateOn.includes('change')
    );

    this.handleValueChange(value as unknown as Value, {
      shouldCommit,
      updateType: 'change',
      event: event as unknown as ChangeEvent<E>,
    });
  };

  handleBlur = (event: FocusEvent<ElementWithValue<Value>>) => {
    const { updateOn } = this.props;
    const { value } = event.target;

    const shouldCommit = (
      updateOn.includes('blur')
    );

    this.handleValueChange(value as unknown as Value, {
      shouldCommit,
      updateType: 'blur',
      event: event as unknown as ChangeEvent<E>,
    });
  };

  handleValueChange = (value: Value | StateValue, { shouldCommit = true, updateType = 'blur', event }: ControlOnValueChangeOptions<E> = {}) => {
    const { formattedValue, displayValue } = this.formatValueToState(value, { updateType });

    const newState = this.updateState(formattedValue, { updateType });
    this.updateValue(formattedValue, newState, { displayValue, shouldCommit, updateType, event });
  };

  handleTouched = (value: Value, { shouldCommit }: ControlOnTouchedOptions = {}) => {
    this.handleValueChange(value, { shouldCommit, updateType: 'blur' });
  };

  commitControlValue = (value: StateValue | Value) => {
    const { name, setControlValue } = this.props;

    if (!isEqual(value, this.lastCommittedValue)) {
      this.preCommittedValue = value;
      setControlValue(name, value, () => {
        this.lastCommittedValue = value;
      });
    }
  };

  commitControlState = (state: ControlFormState) => {
    const { name, setControlState } = this.props;

    setControlState(name, state);
  };

  handleKeyDown = (event: FixedKeyboardEvent) => {
    const { target } = event;

    if (typeof this.pendingCaretChange !== 'undefined') {
      this.positionCaret(target, this.pendingCaretChange);
    }

    this.keyDownValue = event.target.value || '';
    this.keyDownCode = event.keyCode;
  };

  resolveErrors = memoize((errors: ErrorMap, clientErrors: ErrorMap) => (
    merge({}, errors, clientErrors) as ErrorMap
  ));

  resolveWarnings = memoize((warnings: ErrorMap, clientWarnings: ErrorMap) => (
    merge({}, warnings, clientWarnings) as ErrorMap
  ));

  resolveCautions = memoize((cautions: ErrorMap, clientCautions: ErrorMap) => (
    merge({}, cautions, clientCautions) as ErrorMap
  ));

  shouldComponentRerender(prevProps: ControlComposedProps<Value, StateValue, E>, prevState: ControlState<Value>) {
    const renderProps = ['children', 'name', 'errors', 'warnings', 'cautions', 'clientErrors', 'clientWarnings', 'clientCautions', 'isTouched', 'isDirty'];
    const haveRenderPropsChanged = this.havePropsChanged(renderProps, prevProps);

    if (haveRenderPropsChanged) {
      return true;
    }

    const { isTouched } = this.props;
    if (isTouched) {
      const errorsProps = ['validators', 'errorsMarginTop'];
      const haveErrorsPropsChanged = this.havePropsChanged(errorsProps, prevProps);
      if (haveErrorsPropsChanged) {
        return true;
      }
    }

    const { formattedValue } = this.state;
    if (!isEqual(formattedValue, prevState.formattedValue)) {
      return true;
    }

    return false;
  }

  havePropsChanged(propNames: string[], prevProps: ControlComposedProps<Value, StateValue, E>) {
    return propNames.some((propName) => {
      const { [propName]: currentProp } = this.props as { [index: string]: unknown};
      return (prevProps as unknown as { [index: string]: unknown })[propName] !== currentProp;
    });
  }

  formatStateToValue(value: StateValue | Value, { updateType, event, input }: ControlStateToValueFormatOptions<E> = {}) {
    const { formatStateToValue, stateToValueFormatters = [] } = this.props;

    const formattedValue = formatStateToValue ? formatStateToValue(value) : value;

    return stateToValueFormatters.reduce((currentFormattedValue, formatterConfig) => {
      const { deferred, formatter, options } = (typeof formatterConfig === 'function') ?
        { deferred: false, formatter: formatterConfig, options: {} } :
        formatterConfig;

      return (deferred && updateType === 'change') ? currentFormattedValue : formatter(currentFormattedValue as StateValue, { event, input, options });
    }, formattedValue) as Value;
  }

  formatValueToState(value: Value | StateValue, { updateType }: ControlValueToStateFormatOptions) {
    const { formatValueToState, valueToStateFormatters = [] } = this.props;

    const formattedValue = formatValueToState ? formatValueToState(value) : value;

    return valueToStateFormatters.reduce(({ formattedValue: currentFormattedValue, displayValue }, formatterConfig) => {
      const { deferred, formatter, options } = (typeof formatterConfig === 'function') ?
        { deferred: false, formatter: formatterConfig, options: {} } :
        formatterConfig;

      return {
        formattedValue: formatter(currentFormattedValue as Value, { options }),
        displayValue: (deferred && updateType === 'change') ? displayValue : formatter(displayValue as Value, { options }),
      };
    }, { formattedValue, displayValue: formattedValue }) as { formattedValue: StateValue; displayValue: Value };
  }

  refreshValueFormat() {
    const resolvedValue = this.getResolvedValue();
    const newFormattedValue = this.formatStateToValue(resolvedValue);
    this.setState({
      formattedValue: newFormattedValue,
    });
  }

  handleValueUpdate(prevProps: ControlComposedProps<Value, StateValue, E>) {
    const { value, defaultValue } = this.props;

    if (!isEqual(value, prevProps.value) && !isEqual(this.lastCommittedValue, value)) {
      const resolvedNewValue = (typeof value === 'undefined') ? defaultValue : value;
      const updateType = this.isStillFocused() ? 'change' : 'blur';
      this.preCommittedValue = undefined;
      this.lastCommittedValue = value;
      this.handleValueChange(resolvedNewValue, { shouldCommit: true, updateType });
      return true;
    } if (isEqual(value, this.preCommittedValue)) {
      this.preCommittedValue = undefined;
    }

    return false;
  }

  handleValidationChange(prevProps: ControlComposedProps<Value, StateValue, E>) {
    const { validate, validators, value, invalidValue } = this.props;

    const hasAlreadyUpdatedValue = (!isEqual(value, prevProps.value) && !isEqual(this.lastCommittedValue, value));

    if (prevProps.validate !== validate && !hasAlreadyUpdatedValue) {
      const resolvedValue = this.getResolvedValue();
      const newState = this.reduceErrors({}, resolvedValue);

      if (prevProps.validators !== validators) {
        const currentValidatorNames = Object.keys(validators);
        const prevValidatorNames = Object.keys(prevProps.validators);

        const removedValidatorNames = prevValidatorNames.filter((validatorName) => (
          !currentValidatorNames.includes(validatorName)
        ));

        if (removedValidatorNames.length) {
          removedValidatorNames.forEach((validatorName) => {
            const validator = prevProps.validators[validatorName];

            const validatorType: 'errors' | 'warnings' | 'cautions' = (typeof validator === 'object' && validator.type) ? `${validator.type}s` : 'errors';

            if (newState[validatorType]) {
              newState[validatorType][validatorName] = undefined;
            }
          });
        }
      }

      if (newState.errors) {
        this.commitControlState(newState);

        const isValid = checkIsValid(newState.errors);

        if (isValid && isEqual(value, invalidValue) && !isEqual(value, resolvedValue)) {
          this.updateValidValue(resolvedValue, { shouldCommit: true, displayValue: resolvedValue });
        }

        if (!isValid && !isEqual(value, invalidValue)) {
          this.updateInvalidValue(resolvedValue);
        }
      } else if (newState.warnings || newState.cautions) {
        this.commitControlState(newState);
      }
    }
  }

  handleSubmittedChange(prevProps: ControlComposedProps<Value, StateValue, E>) {
    const { isSubmitted, isTouched } = this.props;

    if (prevProps.isSubmitted !== isSubmitted && !isTouched) {
      this.commitControlState({ touched: true });
    }
  }

  updateValue(value: StateValue, newState: ControlFormState, { shouldCommit, displayValue, updateType, event }: ControlUpdateValueOptions<Value, StateValue, E>) {
    const { errors } = this.props;
    const { formattedValue } = this.state;

    const upcomingErrors = merge(errors, newState.errors) as ErrorMap;

    if (checkIsValid(upcomingErrors)) {
      this.updateValidValue(value, { shouldCommit, displayValue });
    } else {
      this.updateInvalidValue(displayValue);
    }

    const input = this.getEventInput(event as ChangeEvent<HTMLInputElement>);
    const newFormattedValue = this.formatStateToValue(displayValue, { updateType, event, input });

    if (!isEqual(formattedValue, newFormattedValue)) {
      this.setState({
        formattedValue: newFormattedValue,
      });
    }

    this.updateCaretPosition(event as FixedKeyboardEvent, input, newFormattedValue as unknown as string);
  }

  updateCaretPosition(event: FixedKeyboardEvent, input: string, newFormattedValue: string) {
    if (event && event.nativeEvent && event.nativeEvent.type === 'input') {
      const { target } = event;
      const { selectionStart, value: previousValue } = target;
      const inputDifference = target.value.length - this.keyDownValue.length;
      const inputStart = selectionStart - inputDifference;

      let newPosition = selectionStart;

      if (input) {
        if (previousValue.length > newFormattedValue.length) {
          const removedCharacterCount = previousValue.length - newFormattedValue.length;
          const keptCharacterCount = inputDifference - removedCharacterCount;
          // If insertered characters are the same as the following character, we let the caret at the end of input
          if (newFormattedValue.substr(selectionStart - removedCharacterCount, removedCharacterCount) !== input.substr(keptCharacterCount)) {
            newPosition = selectionStart - removedCharacterCount;
          }
        } else if (selectionStart === previousValue.length) {
          newPosition = newFormattedValue.length;
        } else {
          const addedCharacterCount = newFormattedValue.length - previousValue.length;
          let currentInput = input;
          const insertPosition = Array.from(newFormattedValue.substring(inputStart)).findIndex((letter) => {
            const nextInput = currentInput[0];

            if (letter === nextInput) {
              if (currentInput.length === 1) {
                return true;
              }

              currentInput = currentInput.substring(1);
              return false;
            }

            return false;
          });

          newPosition = insertPosition === -1 ? selectionStart + addedCharacterCount : insertPosition + inputStart + 1;
        }
      } else if (this.keyDownCode === KEY_CODES.DELETE) {
        if (newFormattedValue.length === previousValue.length + 1) {
          newPosition = selectionStart + 1;
        }
      }

      this.pendingCaretChange = newPosition;

      const hasPendingCaretRequest = (typeof this.caretPositionRequest !== 'undefined');
      if (hasPendingCaretRequest) {
        window.cancelAnimationFrame(this.caretPositionRequest);
      }

      this.caretPositionRequest = window.requestAnimationFrame(() => {
        this.positionCaret(target, newPosition);
      });
    }
  }

  isStillFocused() {
    return (window.document.activeElement === this.inputRef.current as Element);
  }

  positionCaret(target: HTMLInputElement, position: number) {
    if (target.type !== 'number' && this.isStillFocused()) {
      target.selectionStart = position;
      target.selectionEnd = position;
    }

    delete this.pendingCaretChange;
  }

  updateValidValue(value: Value | StateValue, { shouldCommit, displayValue }: ControlUpdateInvalidValueOptions<Value, StateValue>) {
    const { uncommittedValue } = this;

    if (shouldCommit) {
      this.commitControlValue(value);
    }

    if (!shouldCommit) {
      this.uncommittedValue = value;
    } else if (!isEqual(value, displayValue)) {
      this.uncommittedValue = displayValue;
    } else if (typeof uncommittedValue !== 'undefined') {
      this.uncommittedValue = undefined;
    }
  }

  updateInvalidValue(value: Value | StateValue) {
    const { invalidValue, allowInvalid } = this.props;

    if (allowInvalid) {
      this.commitControlValue(value);
      this.uncommittedValue = undefined;
    } else {
      this.commitControlValue(invalidValue);
      this.uncommittedValue = value;
    }
  }

  updateState(value: StateValue, { updateType }: ControlUpdateStateOptions) {
    const newState = this.reduceUpdatedState(value, { updateType });
    const hasStateChanges = Object.keys(newState).length;

    if (hasStateChanges) {
      this.commitControlState(newState);
    }

    return newState;
  }

  reduceUpdatedState(value: StateValue, { updateType }: ControlReduceUpdatedStateOptions) {
    return compose(
      (state: ControlFormState) => this.reduceTouched(state, value, { updateType }),
      (state: ControlFormState) => this.reduceDirty(state, value),
      (state: ControlFormState) => this.reduceErrors(state, value),
    )({});
  }

  reduceTouched(state: ControlFormState, value: StateValue, { updateType }: ControlReduceTouchedOptions) {
    const { isTouched, isInitialized } = this.props;

    if (updateType === 'blur' && isInitialized) {
      return reduceTouched(state, isTouched);
    }

    return state;
  }

  reduceDirty(state: ControlFormState, value: StateValue) {
    const { isDirty } = this.props;
    const { resolvedInitialValue } = this;
    return reduceDirty<Value, StateValue>(state, isDirty, value, resolvedInitialValue);
  }

  reduceErrors(state: ControlFormState, value: Value | StateValue) {
    const { errors, warnings, cautions } = this.props;
    const { errors: newErrors, warnings: newWarnings, cautions: newCautions } = this.validate(value);

    return compose(
      (currentState: ControlFormState) => reduceErrors(currentState, errors, newErrors),
      (currentState: ControlFormState) => reduceWarnings(currentState, warnings, newWarnings),
      (currentState: ControlFormState) => reduceCautions(currentState, cautions, newCautions),
    )(state);
  }

  registerControlWithParentForm() {
    const {
      controlId, name, validators, registerControl, registerControlGroup, hideHoistedAnchor, validationMessages, colLabel, formScope, grouped,
    } = this.props;

    registerControl({
      name,
      controlId,
      label: colLabel,
      id: this.id,
      validate: this.validate,
    });

    if (registerControlGroup && !grouped) {
      registerControlGroup({
        controlId,
        name,
        validationMessages,
        validators,
        label: colLabel,
        id: this.id,
        errorMessagesId: this.errorMessagesId,
        formScope,
        hideHoistedAnchor,
      });
    }
  }

  unregisterControl() {
    const { controlId, name, unregisterControl, unregisterControlGroup } = this.props;
    unregisterControl({ controlId, name });
    if (unregisterControlGroup) unregisterControlGroup(controlId);
  }

  filterErrors(errors: ErrorMap) {
    const { hideErrorMessages } = this.props;

    return Array.isArray(hideErrorMessages) ?
      Object.keys(errors).reduce<ErrorMap>((result, key) => {
        if (!hideErrorMessages.includes(key)) {
          result[key] = errors[key];
        }
        return result;
      }, {})
      : errors;
  }

  render() {
    if (this.rerender !== false) {
      const {
        children, name, errors, warnings, cautions, validators, isTouched, validateInitialValue, isDirty, errorsMarginTop, readOnly, textOnly,
        maxErrorMessageRows, validationMessages, clientErrors, clientWarnings, clientCautions, isControlGroup, isUnionControlGroup, hideErrorMessages, forceInvalid, forceWarning, forceCaution,
      } = this.props;

      const defaultedHideErrorMessages = isControlGroup && typeof hideErrorMessages === 'undefined' ? true : hideErrorMessages;

      const { formattedValue } = this.state;

      const resolvedErrors = this.resolveErrors(errors, clientErrors);
      const resolvedWarnings = this.resolveWarnings(warnings, clientWarnings);
      const resolvedCautions = this.resolveCautions(cautions, clientCautions);
      const filteredErrors = this.filterErrors(resolvedErrors);
      const filteredWarnings = this.filterErrors(resolvedWarnings);
      const filteredCautions = this.filterErrors(resolvedCautions);

      const hasErrors = defaultedHideErrorMessages !== true && (isTouched || validateInitialValue) && !readOnly && !textOnly;

      this.lastRender = (
        <>
          {children({
            name,
            id: this.id,
            value: formattedValue,
            errors: resolvedErrors,
            warnings: resolvedWarnings,
            cautions: resolvedCautions,
            isValid: checkIsValid(resolvedErrors) && !forceInvalid,
            hasWarning: !checkIsValid(resolvedWarnings) || forceWarning,
            hasCaution: !checkIsValid(resolvedCautions) || forceCaution,
            isTouched,
            validateInitialValue,
            isDirty,
            inputRef: this.inputRef,
            errorMessagesId: hasErrors ? this.errorMessagesId : undefined,
            onChange: this.handleChange,
            onKeyDown: this.handleKeyDown,
            onValueChange: this.handleValueChange,
            onBlur: this.handleBlur,
            onTouched: this.handleTouched,
            isUnionControlGroup,
          })}
          {hasErrors && (
            <StyledErrorMessages<Value | StateValue>
              id={this.errorMessagesId}
              name={name}
              value={formattedValue}
              errors={filteredErrors}
              warnings={filteredWarnings}
              cautions={filteredCautions}
              validators={validators}
              errorsMarginTop={errorsMarginTop}
              maxMessageRows={maxErrorMessageRows}
              messages={validationMessages}
            />
          )}
        </>
      );
    }

    return this.lastRender;
  }

  static displayName = 'Control';
  static defaultProps = ControlDefaultProps;
  static publicProps = ['name', 'validators', 'validate', 'validateInitialValue', 'defaultValue', 'formatValueToState', 'valueToStateFormatters', 'formatStateToValue', 'stateToValueFormatters', 'updateOn', 'readOnly', 'textOnly', 'maxErrorMessageRows', 'validationMessages', 'hideErrorMessages', 'allowInvalid', 'colLabel', 'id', 'hideHoistedAnchor', 'forceInvalid', 'forceWarning', 'forceCaution'] as (keyof ControlProps<any, any>)[];
}

export default Control;
